"use client";

import { useMemo, useState } from "react";
import { usePostHog } from "posthog-js/react";
import {
  RACE_DISTANCES,
  distanceByKey,
  formatDuration,
  formatPace,
  parseDuration,
  predictAllDistances,
  predictionBand,
  vdotFromEffort,
} from "@/lib/seo/pace";
import type { SeoLocale } from "@/lib/seo/config";

const COPY = {
  recentDistance: { en: "A distance you've raced", pt: "Uma distância que você correu" },
  recentTime: { en: "Your time", pt: "Seu tempo" },
  predict: { en: "Predict my times", pt: "Prever meus tempos" },
  results: { en: "Your predicted race times", pt: "Seus tempos previstos" },
  distance: { en: "Distance", pt: "Distância" },
  prediction: { en: "Predicted range", pt: "Faixa prevista" },
  pace: { en: "Pace", pt: "Ritmo" },
  vdot: { en: "Estimated VDOT", pt: "VDOT estimado" },
  disclaimer: {
    en: "Predictions are an estimate from a single result and assume similar conditions and specific training. We always show a range, not a single number — the longer the jump from your input, the wider it gets.",
    pt: "As previsões são uma estimativa a partir de um único resultado e assumem condições semelhantes e treino específico. Sempre mostramos uma faixa, não um número único — quanto maior o salto, mais larga ela fica.",
  },
  invalid: {
    en: "Enter a valid time like 0:22:30",
    pt: "Insira um tempo válido como 0:22:30",
  },
};

export function RaceTimePredictor({ locale }: { locale: SeoLocale }) {
  const posthog = usePostHog();
  const [distanceKey, setDistanceKey] = useState("5k");
  const [time, setTime] = useState("00:22:30");
  const [submitted, setSubmitted] = useState<{
    seconds: number;
    meters: number;
  } | null>({ seconds: 1350, meters: 5000 });

  const t = (k: keyof typeof COPY) => COPY[k][locale];

  const error = useMemo(() => {
    if (time.trim() === "") return null;
    return parseDuration(time) === null ? COPY.invalid[locale] : null;
  }, [time, locale]);

  const onPredict = () => {
    const seconds = parseDuration(time);
    const dist = distanceByKey(distanceKey);
    if (seconds === null || !dist) return;
    setSubmitted({ seconds, meters: dist.meters });
    posthog?.capture("seo_tool_use", {
      seo_tool: "race-time-predictor",
      seo_locale: locale,
      distance: distanceKey,
    });
  };

  const result = useMemo(() => {
    if (!submitted) return null;
    const predictions = predictAllDistances(submitted.seconds, submitted.meters);
    const vdot = vdotFromEffort(submitted.seconds, submitted.meters);
    return { predictions, vdot, knownMeters: submitted.meters };
  }, [submitted]);

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm font-medium">{t("recentDistance")}</span>
          <select
            value={distanceKey}
            onChange={(e) => setDistanceKey(e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
          >
            {RACE_DISTANCES.map((d) => (
              <option key={d.key} value={d.key}>
                {locale === "en" ? d.labelEn : d.labelPt}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-medium">{t("recentTime")}</span>
          <input
            type="text"
            inputMode="numeric"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            placeholder="0:22:30"
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
          />
        </label>
      </div>

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      <button
        type="button"
        onClick={onPredict}
        className="mt-5 inline-flex items-center rounded-md bg-primary px-5 py-2.5 font-medium text-primary-foreground hover:opacity-90"
      >
        {t("predict")}
      </button>

      {result && (
        <div className="mt-8">
          {result.vdot && (
            <p className="mb-4 text-sm text-muted-foreground">
              {t("vdot")}:{" "}
              <span className="font-semibold text-foreground">
                {result.vdot.toFixed(1)}
              </span>
            </p>
          )}
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">{t("distance")}</th>
                  <th className="px-4 py-2 font-medium">{t("prediction")}</th>
                  <th className="px-4 py-2 font-medium">{t("pace")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {result.predictions.map((p) => {
                  const ratio = p.distance.meters / result.knownMeters;
                  const band = predictionBand(p.finishSeconds, ratio);
                  return (
                    <tr key={p.distance.key}>
                      <td className="px-4 py-3 font-medium">
                        {locale === "en"
                          ? p.distance.labelEn
                          : p.distance.labelPt}
                      </td>
                      <td className="px-4 py-3 font-mono">
                        {formatDuration(band.low)} – {formatDuration(band.high)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {formatPace(p.perKm)}/km
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">{t("disclaimer")}</p>
        </div>
      )}
    </div>
  );
}
